import {
  InjectionKey,
  provide,
  inject,
  ref,
  Ref,
  computed,
  watch
} from '@vue/composition-api'
import Router, { Route } from 'vue-router'

type Result = {
  username: Readonly<Ref<string | null>>
  userToken: Readonly<Ref<string | null>>
  isLoggedIn: Ref<boolean>
  login: (username: string, password: string) => void
  logout: () => void
}

export const key: InjectionKey<Result> = Symbol('auth')

const USER_TOKEN_KEY = 'storage:user_token'
const USERNAME_KEY = 'storage:username'

export const getToken = (): string | null =>
  localStorage.getItem(USER_TOKEN_KEY)

export const removeToken = (): void => localStorage.removeItem(USER_TOKEN_KEY)

export function provideAuth(): { guardRouter: (router: Router) => void } {
  const userToken: Ref<null | string> = ref(
    localStorage.getItem(USER_TOKEN_KEY)
  )
  const usernameRef: Ref<null | string> = ref(
    localStorage.getItem(USERNAME_KEY)
  )
  const login = (username: string, password: string): void => {
    usernameRef.value = username
    userToken.value = password
  }
  const logout = (): void => {
    usernameRef.value = null
    userToken.value = null
  }

  watch(
    usernameRef,
    username => {
      if (username != null) {
        localStorage.setItem(USERNAME_KEY, username)
      } else {
        localStorage.removeItem(USERNAME_KEY)
      }
    },
    { immediate: true }
  )
  watch(
    userToken,
    userToken => {
      if (userToken != null) {
        localStorage.setItem(USER_TOKEN_KEY, userToken)
      } else {
        localStorage.removeItem(USER_TOKEN_KEY)
      }
    },
    { immediate: true }
  )

  const isLoggedIn = computed(() => userToken.value != null)

  provide(key, {
    username: usernameRef,
    userToken,

    isLoggedIn,

    login,
    logout
  })

  const guardRouter = (router: Router): void => {
    // eslint-disable-next-line
    const guard = (to: Route, _from: Route, next: any): void => {
      if (isLoggedIn.value === false && to.name !== 'login') {
        next({ name: 'login' })
      } else if (isLoggedIn.value && to.name === 'login') {
        next({ name: 'dashboard' })
      } else {
        next()
      }
    }

    router.beforeResolve(guard)

    router.beforeEach(guard)
  }

  return {
    guardRouter
  }
}

export function useAuth(): Result {
  const result = inject(key)
  if (result == null) {
    throw new Error('Auth provider not set')
  }
  return result
}
